/**
 * Indiciile de ecran: „aici faci asta", spus la momentul potrivit.
 *
 * Nu un tur. Un tur rulează la primul login, înainte ca omul să aibă vreo
 * treabă în cap, se dă Next de șase ori și nu rămâne nimic; în plus se agață
 * de butoane anume, iar un buton mutat îl transformă într-un ecran care arată
 * spre nimic. O indicie apare **prima dată când omul deschide ecranul ei**,
 * are o singură propoziție, și dispare pentru totdeauna când o citește.
 *
 * ── De ce stă lista aici, în pachetul de drepturi ───────────────────────
 * Pentru că e lista pe care o validează și serverul: cheile citite de un om
 * se scriu pe contul lui (`staff.look_prefs.hintsSeen`), iar o cheie pe care
 * n-o recunoaște nimeni ar rămâne acolo pentru totdeauna fără să ascundă
 * nimic. O singură listă, citită de amândouă părțile — aceeași regulă ca la
 * permisiuni și la module.
 *
 * ── Ce e o indicie bună ─────────────────────────────────────────────────
 * Un verb și un loc: „apasă «Programare nouă» ca să pui pe cineva în grilă".
 * Nu „bun venit în calendar", nu o descriere a ecranului. Textele stau în
 * traduceri (`dashboard:hint<Cheie>`), ca orice altceva din aplicație.
 *
 * `anchor` e opțional și e doar pentru frumusețe: dacă selectorul se
 * găsește, butonul primește un inel subțire cât timp indicia e pe ecran;
 * dacă nu — pentru că ecranul s-a schimbat — indicia rămâne întreagă și
 * nimic nu se strică. Asta e diferența față de un tur: aici ancora e un
 * adaos, nu temelia.
 */

export interface ScreenHint {
  /** Cheia ținută minte pe contul omului. */
  key: string;
  /** Calea pe care se arată, exact sau ca prefix (`/patients` prinde fișa). */
  path: string;
  /** Permisiunea fără de care ecranul nici nu există pentru el. */
  permission?: string;
  /** Modulul clinicii; stins, indicia nu are ce căuta nicăieri. */
  feature?: string;
  /** Selector CSS al butonului despre care vorbește. Opțional, vezi mai sus. */
  anchor?: string;
}

export const SCREEN_HINTS: ScreenHint[] = [
  {
    key: 'calendar',
    path: '/calendar',
    permission: 'programari.vede',
    anchor: '.cal__newbtn',
  },
  {
    key: 'patients',
    path: '/patients',
    permission: 'pacienti.vede_toti',
  },
  {
    key: 'record',
    path: '/patients/:id',
    permission: 'fisa.vede',
  },
  {
    key: 'payments',
    path: '/payments',
    permission: 'incasari.vede',
  },
  {
    key: 'inventory',
    path: '/inventory',
    permission: 'stoc.vede',
    feature: 'inventory',
  },
  {
    key: 'reports',
    path: '/reports',
    permission: 'rapoarte.vede',
    feature: 'reports',
  },
  {
    key: 'waitlist',
    path: '/waitlist',
    permission: 'programari.vede',
    feature: 'waitlist',
  },
  {
    key: 'hr',
    path: '/hr',
    permission: 'personal.vede',
    feature: 'hr',
  },
];

export const HINT_KEYS: string[] = SCREEN_HINTS.map((h) => h.key);

/**
 * Indicia ecranului curent, dacă omul n-a citit-o deja.
 *
 * Potrivirea pe cale e pe prefix, dar `/patients` și `/patients/:id` sunt
 * două ecrane diferite — lista și fișa — deci fișa se recunoaște după al
 * doilea segment, iar lista numai când nu-l are.
 */
export function hintFor(
  pathname: string,
  opts: {
    seen: readonly string[];
    can: (permission: string) => boolean;
    hasFeature: (feature: string) => boolean;
  },
): ScreenHint | null {
  const segments = pathname.split('/').filter(Boolean);
  for (const hint of SCREEN_HINTS) {
    if (opts.seen.includes(hint.key)) continue;
    const want = hint.path.split('/').filter(Boolean);
    const isRecord = want[want.length - 1] === ':id';
    const base = isRecord ? want.slice(0, -1) : want;
    const sameBase = base.every((part, i) => segments[i] === part);
    if (!sameBase) continue;
    // Fișa cere un segment în plus; lista îl cere lipsă.
    if (isRecord ? segments.length <= base.length : segments.length > base.length) continue;
    if (hint.permission && !opts.can(hint.permission)) continue;
    if (hint.feature && !opts.hasFeature(hint.feature)) continue;
    return hint;
  }
  return null;
}
